import { parse, unparse } from 'papaparse';

/**
 * Shape the raw CSV string returned by `UsersService.exportUsers` into the
 * canonical Users-export CSV. Lifted verbatim from `users.svelte:onExportUsers`
 * so the Users page and the group dynamic-members "Export Users" flow produce
 * byte-for-byte identical output.
 *
 * @param {string} rawCsv - CSV string from the export endpoint.
 * @param {string[]} fields - domain user fields from `DomainService.getFields()`.
 *   Ignored when `includeDiagnostics` is true. A non-array value throws here (as
 *   the pre-extraction code did) rather than silently emitting a username-only
 *   CSV — the caller turns that throw into a visible failure toast.
 * @param {{ includeDiagnostics?: boolean }} [opts]
 * @returns {string} the final CSV string (papaparse `unparse` output).
 */
export const buildUserExportCsv = (rawCsv, fields, { includeDiagnostics = false } = {}) => {
  const parsed = parse(rawCsv, { header: true });
  const sortedData = parsed.data.sort((a, b) => (a.email || '').localeCompare(b.email || ''));

  if (includeDiagnostics) {
    return unparse(sortedData);
  }

  const priorityColumns = ['email', 'name', 'firstname', 'lastname'];

  // Clean, sorted field list for CSV headers:
  const orderedFields = [
    'username',
    ...priorityColumns.filter((col) => fields.includes(col)),
    ...fields.filter((col) => !priorityColumns.includes(col))
  ];

  // Column priority overrides:
  const prioritySuffixes = ['-forced', '-unforced', '-edited', ''];

  const transformedData = sortedData.map((row) => {
    const processedRow = {};

    orderedFields.forEach((field) => {
      // Find the first override (or base field) that contains a non-empty value
      const winningSuffix = prioritySuffixes.find((sfx) => {
        const val = row[`${field}${sfx}`];
        return val !== undefined && val !== null && val !== '';
      });

      // If match is found, use it, otherwise default to empty string
      processedRow[field] = winningSuffix !== undefined ? row[`${field}${winningSuffix}`] : '';
    });

    return processedRow;
  });

  return unparse({ fields: orderedFields, data: transformedData });
};
